<template>
  <v-container class="faqs-container">
    <v-card class="elevation-3 rounded-lg pa-4">
      <div class="faqs">
        <div class="get-the-loan">
          <!-- Application FAQs Section -->
          <h2 class="text-h5 font-weight-bold primary--text mb-4">
            Application FAQs
          </h2>

          <v-expansion-panels flat>
            <!-- First FAQ Item -->
            <v-expansion-panel>
              <v-expansion-panel-header
                class="text-subtitle-1 font-weight-bold primary--text"
              >
                How do I apply for my first loan with Century Financial
                Services?
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card-text>
                  <p class="body-1 mb-3">
                    Getting started is quick and easy! To apply for your first
                    loan, you'll need to provide the following:
                  </p>
                  <v-list dense class="flex-wrap">
                    <v-list-item
                      v-for="(item, i) in firstLoanRequirements"
                      :key="i"
                      class="pa-0"
                    >
                      <v-list-item-icon class="me-2 my-1">
                        <v-icon color="primary">mdi-check-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-1">
                        <v-list-item-title
                          v-html="item"
                          class="text-wrap"
                        ></v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                  <p class="body-1 mt-3">
                    Once submitted, our platform will guide you seamlessly
                    through the rest of the process.
                  </p>
                </v-card-text>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <!-- Second FAQ Item -->
            <v-expansion-panel>
              <v-expansion-panel-header
                class="text-subtitle-1 font-weight-bold primary--text"
              >
                How do I apply for another loan as a returning customer?
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card-text>
                  <p class="body-1 mb-3">
                    If you've borrowed from us before, applying again is even
                    simpler:
                  </p>
                  <v-stepper vertical>
                    <v-stepper-step step="1" complete>
                      Click the <strong>"Log In"</strong> button and enter the
                      <strong>mobile number</strong> you used during
                      registration.
                    </v-stepper-step>
                    <v-stepper-content step="1"> </v-stepper-content>
                    <v-stepper-step step="2" complete>
                      Verify your personal and banking details.
                    </v-stepper-step>
                    <v-stepper-content step="2"> </v-stepper-content>
                    <v-stepper-step step="3" complete>
                      Confirm your income and monthly expenses
                    </v-stepper-step>
                    <v-stepper-content step="3">
                      <v-card flat>
                        <v-card-text>
                          <v-list dense class="flex-wrap">
                            <v-list-item>
                              <v-list-item-icon class="me-2 my-1">
                                <v-icon small>mdi-circle-small</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content class="py-1">
                                <v-list-item-title class="text-wrap"
                                  >Upload
                                  <strong>bank statements</strong> reflecting
                                  the
                                  <strong
                                    >last 3 months' income</strong
                                  ></v-list-item-title
                                >
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                              <v-list-item-icon class="me-2 my-1">
                                <v-icon small>mdi-circle-small</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content class="py-1">
                                <v-list-item-title class="text-wrap"
                                  >Complete the online form with your
                                  <strong
                                    >monthly expense details</strong
                                  ></v-list-item-title
                                >
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-card-text>
                      </v-card>
                    </v-stepper-content>
                    <v-stepper-step step="4" complete>
                      Choose your desired loan amount
                    </v-stepper-step>
                    <v-stepper-content step="4"> </v-stepper-content>
                    <v-stepper-step step="5" complete>
                      Read the <strong>Pre-Agreement</strong> and
                      <strong>Loan Agreement</strong>
                    </v-stepper-step>
                    <v-stepper-content step="5"> </v-stepper-content>
                    <v-stepper-step step="6" complete>
                      Click <strong>"I Accept"</strong> to confirm — the funds
                      will then be processed and paid into your bank account
                    </v-stepper-step>
                    <v-stepper-content step="6"> </v-stepper-content>
                  </v-stepper>
                </v-card-text>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <!-- Third FAQ Item -->
            <v-expansion-panel>
              <v-expansion-panel-header
                class="text-subtitle-1 font-weight-bold primary--text"
              >
                How quickly will I receive the funds?
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card-text>
                  <p class="body-1 mb-3">
                    Once your loan is approved, funds are transferred directly
                    into your <strong>verified bank account</strong>.
                  </p>
                  <h4 class="subtitle-1 font-weight-medium mb-2">
                    :stopwatch: Processing times:
                  </h4>
                  <v-card outlined class="mb-4">
                    <v-list dense class="flex-wrap">
                      <v-list-item>
                        <v-list-item-icon class="me-2 my-1">
                          <v-icon color="primary">mdi-clock-fast</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content class="py-1">
                          <v-list-item-title class="text-wrap"
                            ><b>Before 14:00 (Monday to Friday)</b>: Funds are
                            typically credited by <b>19:00</b> the same business
                            day</v-list-item-title
                          >
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-icon class="me-2 my-1">
                          <v-icon color="warning">mdi-clock-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content class="py-1">
                          <v-list-item-title class="text-wrap"
                            ><b>After 14:00 or on weekends/public holidays</b>:
                            Funds are credited by <b>19:00</b> on the
                            <b>next business day</b></v-list-item-title
                          >
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-card-text>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <!-- Fourth FAQ Item -->
            <v-expansion-panel>
              <v-expansion-panel-header
                class="text-subtitle-1 font-weight-bold primary--text"
              >
                Why do you request additional documents?
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card-text>
                  <p class="body-1 mb-3">
                    To assess your application responsibly and in compliance
                    with the <strong>National Credit Act</strong>, we may need
                    to request additional documents to:
                  </p>
                  <v-list dense class="flex-wrap">
                    <v-list-item
                      v-for="(reason, i) in documentReasons"
                      :key="i"
                    >
                      <v-list-item-icon class="me-2 my-1">
                        <v-icon color="primary"
                          >mdi-file-document-outline</v-icon
                        >
                      </v-list-item-icon>
                      <v-list-item-content class="py-1">
                        <v-list-item-title
                          v-html="reason"
                          class="text-wrap"
                        ></v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                  <p class="body-1 mt-3">
                    This ensures we offer you a loan that suits your financial
                    situation.
                  </p>
                </v-card-text>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <!-- Fifth FAQ Item -->
            <v-expansion-panel>
              <v-expansion-panel-header
                class="text-subtitle-1 font-weight-bold primary--text"
              >
                Is it safe to share my bank account details?
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card-text>
                  <v-alert text type="info" class="mb-3">
                    <p class="body-1">
                      Yes, it's completely safe.<br />
                      We use <strong>encrypted pages</strong> and advanced
                      <strong>cybersecurity tools</strong> to ensure your
                      information is secure at every step of the process.
                    </p>
                  </v-alert>
                </v-card-text>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <!-- Sixth FAQ Item -->
            <v-expansion-panel>
              <v-expansion-panel-header
                class="text-subtitle-1 font-weight-bold primary--text"
              >
                Why was my loan application declined?
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card-text>
                  <p class="body-1 mb-3">
                    Common reasons for an application not being approved
                    include:
                  </p>
                  <v-list dense class="flex-wrap">
                    <v-list-item v-for="(reason, i) in declineReasons" :key="i">
                      <v-list-item-icon class="me-2 my-1">
                        <v-icon color="error">mdi-alert-circle-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-1">
                        <v-list-item-title
                          v-html="reason"
                          class="text-wrap"
                        ></v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                  <v-alert text outlined type="info" class="mt-3">
                    <p class="body-1">
                      We recommend double-checking all information and trying
                      again. If you need help, our support team is ready to
                      assist you.
                    </p>
                  </v-alert>
                </v-card-text>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: "FaqsComponent",
  data() {
    return {
      firstLoanRequirements: [
        "<strong>Your South African ID number</strong>",
        "<strong>Cell phone number</strong>",
        "<strong>Email address</strong>",
        "<strong>Bank account details</strong>",
        "<strong>Employment information</strong>",
      ],
      documentReasons: [
        "<strong>Confirm your identity</strong>",
        "<strong>Verify your income</strong>",
        "<strong>Assess affordability</strong>",
      ],
      declineReasons: [
        "<strong>Incorrect or incomplete information</strong> submitted",
        "<strong>Inconsistencies</strong> in financial data",
        "<strong>Affordability concerns</strong> based on your supporting documents",
      ],
    };
  },
};
</script>

<style scoped>
.faqs-container {
  width: 100%;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 24px;
}
</style>
